import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Helmet } from "react-helmet-async";
import AuthService from "../../../api/services/AuthService";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import { SocketContext } from "../../../customComponents/SocketContext";
import TVChartContainer from "../../../customComponents/Libraries/TVChartContainer";
import TradePairsSection from "../TradePage/components/TradePairsSection";
import TradeAmountDenomSelect from "../TradePage/TradeAmountDenomSelect";
import OrderMobileCardShell from "../Order/OrderMobileCardShell";
import useOtcDesk from "./useOtcDesk";
import OtcAcceptQuoteModal from "./OtcAcceptQuoteModal";
import OtcCancelRfqModal from "./OtcCancelRfqModal";
import "../TradePage/trade_new.css";
import "./otc_rfq.css";

const COIN_ICON_FALLBACKS = {
    btc: "/images/icon/btc.png",
    eth: "/images/icon/eth.png",
    bnb: "/images/icon/bnb.png",
    sol: "/images/icon/sol.png",
    usdt: "/images/icon/tether.png",
    usdc: "/images/icon/usdc.png",
    xrp: "/images/icon/xrp.png",
    ada: "/images/icon/ada.png",
    doge: "/images/icon/doge.png",
    dot: "/images/icon/dot.png",
    ltc: "/images/icon/ltc.png",
    matic: "/images/icon/matic.png",
    shib: "/images/icon/shib.png",
    trx: "/images/icon/trx.png",
    busd: "/images/icon/busd.png",
};

function RfqRowMenu({ open, onToggle, onView, onCancel }) {
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const [pos, setPos] = useState(null);

    useEffect(() => {
        if (!open) return undefined;
        const place = () => {
            const rect = buttonRef.current?.getBoundingClientRect();
            if (!rect) return;
            const menuHeight = 84;
            const spaceBelow = window.innerHeight - rect.bottom;
            const top = spaceBelow < menuHeight + 8 ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4;
            setPos({ top, left: Math.max(8, rect.right - 148) });
        };
        const close = (event) => {
            if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
            onToggle(false);
        };
        const onKey = (event) => {
            if (event.key === "Escape") onToggle(false);
        };
        place();
        document.addEventListener("mousedown", close);
        document.addEventListener("keydown", onKey);
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("resize", place);
            window.removeEventListener("scroll", place, true);
        };
    }, [open, onToggle]);

    return (
        <>
            <button
                type="button"
                className="otc-rfq-menu-btn"
                ref={buttonRef}
                aria-label="Request actions"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => onToggle(!open)}
            >
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <circle cx="8" cy="3.2" r="1.25" fill="currentColor" />
                    <circle cx="8" cy="8" r="1.25" fill="currentColor" />
                    <circle cx="8" cy="12.8" r="1.25" fill="currentColor" />
                </svg>
            </button>
            {open && pos && typeof document !== "undefined" ? createPortal(
                <div
                    ref={menuRef}
                    className="otc-rfq-menu"
                    style={{ top: pos.top, left: pos.left }}
                    role="menu"
                >
                    <button
                        type="button"
                        role="menuitem"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onView();
                        }}
                    >
                        View
                    </button>
                    {onCancel ? (
                        <button
                            type="button"
                            role="menuitem"
                            className="is-cancel"
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onCancel();
                            }}
                        >
                            Cancel
                        </button>
                    ) : null}
                </div>,
                document.body
            ) : null}
        </>
    );
}

function formatWhen(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function formatLeft(ms) {
    const total = Math.max(0, Math.ceil(Number(ms) / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatAmount(value) {
    const text = String(value == null ? "" : value).trim();
    if (!/^\d+(\.\d+)?$/.test(text)) return text;
    const [whole, frac = ""] = text.split(".");
    const cut = frac.slice(0, 8).replace(/0+$/, "");
    return cut ? `${whole}.${cut}` : whole;
}

function moneyLine(amount, asset) {
    if (amount == null || amount === "") return "—";
    const shown = formatAmount(amount);
    if (shown === "") return "—";
    return `${shown} ${asset || ""}`.trim();
}

function formatAvail(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return "0";
    return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function pairsFromResponse(res) {
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.pairs)) return res.data.pairs;
    return [];
}

function pairSymbol(row) {
    const base = String(row?.base_currency || row?.base || "").toUpperCase();
    const quote = String(row?.quote_currency || row?.quote || "").toUpperCase();
    return base && quote ? `${base}/${quote}` : "";
}

function isListedActive(row) {
    const status = String(row?.status || "Active").trim().toUpperCase();
    return !status || status === "ACTIVE";
}

function rfqPair(row) {
    if (row?.pair_base && row?.pair_quote) return `${row.pair_base}/${row.pair_quote}`;
    const side = String(row?.side || "").toUpperCase();
    if (side === "SELL" && row?.pay_asset && row?.get_asset) return `${row.pay_asset}/${row.get_asset}`;
    if (row?.get_asset && row?.pay_asset) return `${row.get_asset}/${row.pay_asset}`;
    return "—";
}

const TRADE_KIND = new Set(["PENDING_SETTLEMENT", "HELD", "SETTLED", "VOID"]);
const LOCKED_TRADE = new Set(["PENDING_SETTLEMENT", "HELD"]);

function ticketLabel(id) {
    const text = String(id || "").trim();
    if (!text) return "—";
    return text.slice(-8).toUpperCase();
}

function toTableRow(row, quote) {
    const status = String(row?.status || "—");
    const statusKey = status.toUpperCase();
    const quotedHere = quote && String(quote.rfq_id || quote.rfqId || "") === String(row?.id);
    const rate = quotedHere ? quote.rate : row?.rate;
    const sideRaw = String(row?.side || "").toUpperCase();
    const isTrade = TRADE_KIND.has(statusKey)
        || (row?.quote_id && row?.rfq_id && String(row.id) !== String(row.rfq_id));
    return {
        id: String(row?.id || rfqPair(row)),
        ticketId: ticketLabel(row?.id),
        date: formatWhen(row?.createdAt),
        time: formatWhen(row?.settled_at || row?.createdAt),
        type: isTrade ? "Trade" : "RFQ",
        pair: rfqPair(row),
        side: sideRaw === "SELL" ? "Sell" : sideRaw === "BUY" ? "Buy" : "—",
        pay: moneyLine(row?.pay_amount, row?.pay_asset),
        receive: moneyLine(row?.net_get_amount || row?.get_amount, row?.get_asset),
        tradedAmount: isTrade || rate ? moneyLine(row?.net_get_amount || row?.get_amount, row?.get_asset) : "—",
        deskPrice: rate || (statusKey === "OPEN" ? "Awaiting desk" : "—"),
        quotePrice: rate || "—",
        limitPrice: String(row?.request_rate || "").trim() || rate || "—",
        averagePrice: rate || "—",
        progress: statusKey === "QUOTED"
            ? "Quoted"
            : statusKey === "OPEN"
                ? "Open"
                : LOCKED_TRADE.has(statusKey)
                    ? "Funds locked"
                    : "—",
        filledTotal: statusKey === "OPEN" || statusKey === "QUOTED"
            ? `0 / ${moneyLine(row?.pay_amount, row?.pay_asset)}`
            : LOCKED_TRADE.has(statusKey)
                ? moneyLine(row?.pay_amount, row?.pay_asset)
                : "—",
        status,
        source: !isTrade && statusKey === "OPEN" ? row : null,
        view: {
            id: String(row?.id || ""),
            ticketId: ticketLabel(row?.id),
            pair: rfqPair(row),
            side: sideRaw === "SELL" ? "Sell" : sideRaw === "BUY" ? "Buy" : "—",
            pay: moneyLine(row?.pay_amount, row?.pay_asset),
            receive: moneyLine(row?.net_get_amount || row?.get_amount, row?.get_asset),
            status,
            requestRate: String(row?.request_rate || "").trim(),
            rate: rate || "",
            expiresAt: row?.expiresAt || (quotedHere ? quote?.expiresAt : null) || null,
            createdAt: row?.createdAt || null,
        },
    };
}

export default function OtcRfqTradingPage() {
    const socketCtx = useContext(SocketContext) || {};
    const { isConnected, exchangeData, subscribeToExchange, unsubscribeFromExchange } = socketCtx;

    const desk = useOtcDesk();

    // Pairs list and Selected Coin state
    const [allPairs, setAllPairs] = useState([]);
    const [CoinPairDetails, setCoinPairDetails] = useState([]);
    const [SelectedCoin, setSelectedCoin] = useState(null);
    const [baseCurId, setBaseCurId] = useState(null);
    const [quoteCurId, setQuoteCurId] = useState(null);

    // Live market stats (like Spot Trade)
    const [displayLastForHeader, setDisplayLastForHeader] = useState("");
    const [priceChange, setPriceChange] = useState(0);
    const [isPricePositive, setIsPricePositive] = useState(true);

    // Dropdown and filter states (matching TradePage)
    const [isFavouritesOpen, setIsFavouritesOpen] = useState(false);
    const [showMobileFavouritesPopup, setShowMobileFavouritesPopup] = useState(false);
    const [coinFilter, setcoinFilter] = useState("USDT");
    const [search, setsearch] = useState("");
    const [favCoins, setfavCoins] = useState([]);
    const token = AuthService.getToken?.() || "";

    // Table tab: "active" | "history"
    const [bottomTab, setBottomTab] = useState("active");
    const [quotePopupOpen, setQuotePopupOpen] = useState(false);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [actionMenuId, setActionMenuId] = useState("");
    const [activeTicketOpen, setActiveTicketOpen] = useState(false);
    const [viewRfq, setViewRfq] = useState(null);
    const openedQuoteIdRef = useRef("");

    // Mobile Buy/Sell Drawer Tab state (like TradePage)
    const [showBuySellTab, setShowBuySellTab] = useState("");
    const [headerPairsOpen, setHeaderPairsOpen] = useState(false);
    const [tradingSearch, setTradingSearch] = useState("");
    const [tradingCoinFilter, setTradingCoinFilter] = useState("USDT");
    const headerPairRef = useRef(null);
    const quoteFormRef = useRef(null);
    const activeTicketRef = useRef(null);
    const selectedPairRef = useRef("");
    const chartIdsReadyRef = useRef(false);

    const liveQuoteId = desk.view === "quote" ? String(desk.quote?.id || "") : "";

    const openCancelConfirm = useCallback((target) => {
        if (!target?.id) return;
        setActionMenuId("");
        window.setTimeout(() => {
            setCancelTarget(target);
            setCancelConfirmOpen(true);
        }, 0);
    }, []);

    useEffect(() => {
        const node = quoteFormRef.current;
        if (!node) return undefined;
        let userMoved = false;
        const stop = () => {
            userMoved = true;
        };
        const reveal = () => {
            if (userMoved) return;
            const header = document.querySelector("header");
            const headerHeight = header ? header.getBoundingClientRect().height : 0;
            const rect = node.getBoundingClientRect();
            const shown = rect.top >= headerHeight && rect.top < headerHeight + 24;
            if (shown) return;
            window.scrollTo({
                top: Math.max(0, rect.top + window.scrollY - headerHeight - 12),
                behavior: "smooth",
            });
        };
        window.addEventListener("wheel", stop, { passive: true });
        window.addEventListener("touchmove", stop, { passive: true });
        const timers = [120, 700].map((ms) => window.setTimeout(reveal, ms));
        return () => {
            userMoved = true;
            timers.forEach((timer) => window.clearTimeout(timer));
            window.removeEventListener("wheel", stop);
            window.removeEventListener("touchmove", stop);
        };
    }, []);

    useEffect(() => {
        if (!liveQuoteId) {
            setQuotePopupOpen(false);
            return;
        }
        if (openedQuoteIdRef.current === liveQuoteId) return;
        openedQuoteIdRef.current = liveQuoteId;
        setQuotePopupOpen(true);
    }, [liveQuoteId]);

    const orderSide = String(desk.quoteType || "buy").toUpperCase() === "SELL" ? "SELL" : "BUY";
    const deskPair = String(desk.pairKey || "BTC/USDT").split("/");
    const baseCoin = deskPair[0] || "BTC";
    const quoteCoin = deskPair[1] || "USDT";
    const payDenom = String(desk.amountAsset || "").toUpperCase() === String(quoteCoin).toUpperCase()
        ? "QUOTE"
        : "BASE";
    const availableText = `${formatAvail(desk.available)} ${desk.availableAsset || (orderSide === "BUY" ? quoteCoin : baseCoin)}`;

    const handleSwitchOrderSide = useCallback((side) => {
        setShowBuySellTab(side.toLowerCase());
        desk.setQuoteType?.(side === "SELL" ? "sell" : "buy");
    }, [desk]);

    // Resolve coin icon helper (with fallback map by symbol)
    const coinIconSrc = useCallback((path, symbol = "") => {
        if (path) {
            const s = String(path);
            if (s.startsWith("http") || s.startsWith("/images/icon/") || s.startsWith("/images/")) {
                if (s.startsWith("/images/coins/")) {
                    const coinName = s.replace("/images/coins/", "").toLowerCase().replace(/\.[a-z]+$/, "");
                    return COIN_ICON_FALLBACKS[coinName] || `/images/icon/${coinName}.png`;
                }
                return s;
            }
            return `${resolvePublicUploadUrl(s.replace(/^\//, ""))}`;
        }
        const sym = String(symbol || "").toLowerCase();
        if (sym && COIN_ICON_FALLBACKS[sym]) {
            return COIN_ICON_FALLBACKS[sym];
        }
        return "/images/icon/btc.png";
    }, []);

    // Load pairs initially via AuthService
    useEffect(() => {
        AuthService.getPairs?.()
            .then((res) => {
                const loaded = pairsFromResponse(res);
                if (loaded.length > 0) {
                    setAllPairs(loaded);
                }
            })
            .catch(() => { });
    }, [desk.pairKey]);

    // Also receive pairs & live ticker from socket exchangeData
    useEffect(() => {
        if (exchangeData?.pairs && Array.isArray(exchangeData.pairs) && exchangeData.pairs.length > 0) {
            setAllPairs(exchangeData.pairs);
        }
        if (exchangeData?.ticker) {
            const t = exchangeData.ticker;
            if (t.lastPrice || t.last || t.buy_price) {
                const p = String(t.lastPrice || t.last || t.buy_price);
                setDisplayLastForHeader(p);
            }
            if (t.change != null || t.change_percentage != null) {
                const ch = Number(t.change_percentage ?? t.change ?? 0);
                setPriceChange(ch);
                setIsPricePositive(ch >= 0);
            }
        }
    }, [exchangeData]);

    // Spot keeps the chart pair ids in their own state. The live pairs list is
    // replaced on every exchange:update, so the subscribe effect must not depend
    // on that list or it unsubscribes on each tick and the candle never moves.
    useEffect(() => {
        const symbol = (
            SelectedCoin?.base_currency && SelectedCoin?.quote_currency
                ? `${SelectedCoin.base_currency}/${SelectedCoin.quote_currency}`
                : String(desk.pairKey || "BTC/USDT")
        ).toUpperCase();
        const pools = [allPairs, desk.spotPairs];
        let row = null;
        for (const pool of pools) {
            const found = (pool || []).find((pair) => pairSymbol(pair) === symbol);
            if (found?.base_currency_id && found?.quote_currency_id) {
                row = found;
                break;
            }
        }
        const baseId = row?.base_currency_id || SelectedCoin?.base_currency_id || null;
        const quoteId = row?.quote_currency_id || SelectedCoin?.quote_currency_id || null;
        if (!baseId || !quoteId) {
            setBaseCurId((prev) => (prev == null ? prev : null));
            setQuoteCurId((prev) => (prev == null ? prev : null));
            return;
        }
        setBaseCurId((prev) => (prev != null && String(prev) === String(baseId) ? prev : baseId));
        setQuoteCurId((prev) => (prev != null && String(prev) === String(quoteId) ? prev : quoteId));
    }, [allPairs, desk.spotPairs, SelectedCoin, desk.pairKey]);

    // Same effect as TradePage: subscribe while these ids are on screen.
    useEffect(() => {
        if (!baseCurId || !quoteCurId || !isConnected || !subscribeToExchange) return undefined;
        subscribeToExchange(baseCurId, quoteCurId);
        return () => {
            if (unsubscribeFromExchange) unsubscribeFromExchange(baseCurId, quoteCurId);
        };
    }, [baseCurId, quoteCurId, isConnected, subscribeToExchange, unsubscribeFromExchange]);

    const listedPairs = useMemo(() => {
        const options = Array.isArray(desk.pairOptions) ? desk.pairOptions : [];
        const allowed = new Set(
            options.map((opt) => String(opt.symbol || "").toUpperCase()).filter(Boolean)
        );
        if (!allowed.size) return [];
        const liveBySymbol = new Map();
        allPairs.forEach((row) => {
            if (!isListedActive(row)) return;
            const symbol = pairSymbol(row);
            if (symbol && allowed.has(symbol)) liveBySymbol.set(symbol, row);
        });
        return options
            .map((opt) => {
                const symbol = String(opt.symbol || "").toUpperCase();
                if (!allowed.has(symbol)) return null;
                const live = liveBySymbol.get(symbol);
                if (live) return live;
                const [base, quote] = symbol.split("/");
                return {
                    base_currency: base,
                    quote_currency: quote,
                    base_currency_fullname: opt.name || base,
                    icon_path: opt.iconPath || "",
                    buy_price: "",
                    change_percentage: 0,
                    status: "Active",
                };
            })
            .filter(Boolean);
    }, [allPairs, desk.pairOptions]);

    useEffect(() => {
        if (!listedPairs.length) return;
        const want = (selectedPairRef.current || String(desk.pairKey || "BTC/USDT")).toUpperCase();
        const match = listedPairs.find((row) => pairSymbol(row) === want)
            || listedPairs.find((row) => pairSymbol(row) === "BTC/USDT")
            || listedPairs[0];
        if (!match) return;
        const symbol = pairSymbol(match);
        const hasIds = Boolean(match.base_currency_id && match.quote_currency_id);
        if (selectedPairRef.current === symbol && chartIdsReadyRef.current === hasIds && hasIds) return;
        selectedPairRef.current = symbol;
        chartIdsReadyRef.current = hasIds;
        setSelectedCoin(match);
        setDisplayLastForHeader(String(match.buy_price || match.sell_price || ""));
        const ch = Number(match.change_percentage ?? 0);
        setPriceChange(Number.isFinite(ch) ? ch : 0);
        setIsPricePositive(ch >= 0);
    }, [listedPairs, desk.pairKey]);

    // Chart menu lists the OTC desk's active pairs, with the live market price on each row.
    useEffect(() => {
        if (!search) {
            setCoinPairDetails(listedPairs);
            return;
        }
        const q = search.toLowerCase();
        setCoinPairDetails(listedPairs.filter((item) => (
            item?.base_currency?.toLowerCase().includes(q)
            || item?.quote_currency?.toLowerCase().includes(q)
            || item?.base_currency_fullname?.toLowerCase().includes(q)
        )));
    }, [search, listedPairs]);

    // This menu only switches the live chart and its price. The OTC pair stays on the form.
    const handleSelectCoin = useCallback((coin) => {
        setSelectedCoin(coin);
        selectedPairRef.current = pairSymbol(coin);
        chartIdsReadyRef.current = Boolean(coin?.base_currency_id && coin?.quote_currency_id);
        setDisplayLastForHeader(String(coin?.buy_price || coin?.sell_price || ""));
        const ch = Number(coin?.change_percentage ?? 0);
        setPriceChange(Number.isFinite(ch) ? ch : 0);
        setIsPricePositive(ch >= 0);
        setIsFavouritesOpen(false);
    }, []);

    const handleTradingPair = useCallback((coin) => {
        const symbol = pairSymbol(coin);
        if (symbol) desk.onPairChange?.(symbol);
        setHeaderPairsOpen(false);
    }, [desk]);

    // Add / remove favorites
    const handleAddFav = useCallback((coinId) => {
        setfavCoins((prev) =>
            prev.includes(coinId) ? prev.filter((id) => id !== coinId) : [...prev, coinId]
        );
        if (token) {
            AuthService.addFavorite?.(coinId).catch(() => { });
        }
    }, [token]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!isFavouritesOpen) return;
            if (
                !e.target.closest(".trade_favourites_lft") &&
                !e.target.closest(".headline_symbolName__KfmIZ")
            ) {
                setIsFavouritesOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isFavouritesOpen]);

    useEffect(() => {
        if (!headerPairsOpen) return undefined;
        const close = (event) => {
            if (headerPairRef.current && !headerPairRef.current.contains(event.target)) {
                setHeaderPairsOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [headerPairsOpen]);

    useEffect(() => {
        if (!activeTicketOpen) return undefined;
        const close = (event) => {
            if (activeTicketRef.current && !activeTicketRef.current.contains(event.target)) {
                setActiveTicketOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [activeTicketOpen]);

    const displayTableRows = useMemo(() => {
        const history = Array.isArray(desk.trades) ? desk.trades : [];
        const status = String(desk.rfq?.status || "").toUpperCase();
        const windowClosed = desk.view === "expired";
        const liveStatus = (row) => {
            const key = String(row?.status || "").toUpperCase();
            return key === "OPEN" || key === "QUOTED";
        };
        const live = [];
        (Array.isArray(desk.liveRequests) ? desk.liveRequests : []).forEach((ticket) => {
            if (ticket?.rfq) live.push({ ...ticket.rfq, rate: ticket.quote?.rate || ticket.rfq.rate });
        });
        history.forEach((row) => {
            if (liveStatus(row)) live.push(row);
        });
        const locked = (row) => LOCKED_TRADE.has(String(row?.status || "").toUpperCase());
        const seen = new Set();
        const take = (row) => {
            if (!row) return false;
            const id = row.id ? String(row.id) : "";
            const rfqKey = row.rfq_id ? `rfq:${row.rfq_id}` : "";
            if ((id && seen.has(id)) || (rfqKey && seen.has(rfqKey))) return false;
            if (id) seen.add(id);
            if (rfqKey) seen.add(rfqKey);
            return true;
        };
        if (bottomTab === "active") {
            const rows = [];
            live.forEach((row) => {
                if (take(row)) rows.push(row);
            });
            history.forEach((row) => {
                if (locked(row) && take(row)) rows.push(row);
            });
            if (locked(desk.trade) && take(desk.trade)) rows.push(desk.trade);
            return rows.map((row) => toTableRow(row, desk.quote));
        }
        const past = history.filter((row) => !locked(row) && !liveStatus(row));
        past.forEach((row) => {
            if (row?.id) seen.add(String(row.id));
            if (row?.rfq_id) seen.add(`rfq:${row.rfq_id}`);
        });
        const extras = [];
        if (desk.trade?.id && !locked(desk.trade) && !seen.has(String(desk.trade.id))) extras.push(desk.trade);
        if (desk.rfq?.id && (windowClosed || (status && status !== "OPEN" && status !== "QUOTED"))) {
            const rfqKey = `rfq:${desk.rfq.id}`;
            const closed = { ...desk.rfq, rate: desk.quote?.rate || desk.rfq.rate };
            if (!locked(closed) && !seen.has(String(desk.rfq.id)) && !seen.has(rfqKey)) extras.push(closed);
        }
        return [...extras, ...past].map((row) => toTableRow(row, desk.quote));
    }, [bottomTab, desk.trades, desk.rfq, desk.quote, desk.trade, desk.view, desk.liveRequests]);

    const activeTickets = useMemo(() => {
        const seen = new Set();
        const list = [];
        const push = (rfq, quote) => {
            if (!rfq?.id || seen.has(String(rfq.id))) return;
            const status = String(rfq.status || "").toUpperCase();
            if (status !== "OPEN" && status !== "QUOTED") return;
            seen.add(String(rfq.id));
            list.push({
                id: String(rfq.id),
                pair: rfqPair(rfq),
                side: String(rfq.side || "").toUpperCase() === "SELL" ? "Sell" : "Buy",
                status,
                pay: moneyLine(rfq.pay_amount, rfq.pay_asset),
                rfq,
                quote: quote?.id ? quote : null,
            });
        };
        (Array.isArray(desk.liveRequests) ? desk.liveRequests : []).forEach((ticket) => {
            push(ticket?.rfq, ticket?.quote);
        });
        (Array.isArray(desk.trades) ? desk.trades : []).forEach((row) => push(row, null));
        return list;
    }, [desk.liveRequests, desk.trades]);

    const activePairSymbol = `${SelectedCoin?.base_currency || deskPair[0] || "BTC"}/${SelectedCoin?.quote_currency || deskPair[1] || "USDT"}`;
    const deskSymbol = String(desk.pairKey || activePairSymbol).toUpperCase();
    const headerPair = listedPairs.find((row) => pairSymbol(row) === deskSymbol) || null;
    const headerPairName = headerPair?.base_currency_fullname || headerPair?.base_currency || deskPair[0] || "BTC";
    const tradingQuery = tradingSearch.trim().toLowerCase();
    const tradingPairs = tradingQuery
        ? listedPairs.filter((row) => (
            row?.base_currency?.toLowerCase().includes(tradingQuery)
            || row?.quote_currency?.toLowerCase().includes(tradingQuery)
            || row?.base_currency_fullname?.toLowerCase().includes(tradingQuery)
        ))
        : listedPairs;

    return (
        <>
            <Helmet>
                <title>{`OTC Trading | ${activePairSymbol} – AGCX`}</title>
                <meta
                    name="description"
                    content="Trade large block orders through our institutional OTC desk with deep liquidity and zero slippage."
                />
            </Helmet>

            <div className="trade-wrapper spot pb-0 pb-lg-3 otc_tradingpage">
                <div className="container-fluid">
                    <div className="row g-1 g-md-2" style={{ position: "relative" }}>
                        {/* Spot Coin Pair Dropdown Section (Opens on BTC/USDT click) */}
                        <TradePairsSection
                            setIsFavouritesOpen={setIsFavouritesOpen}
                            isFavouritesOpen={isFavouritesOpen}
                            token={token}
                            coinFilter={coinFilter}
                            setcoinFilter={setcoinFilter}
                            CoinPairDetails={CoinPairDetails}
                            search={search}
                            setsearch={setsearch}
                            favCoins={favCoins}
                            SelectedCoin={SelectedCoin}
                            handleSelectCoin={handleSelectCoin}
                            coinIconSrc={coinIconSrc}
                            handleAddFav={handleAddFav}
                            hideQuoteTabs
                        />

                        {/* Left / Center Column (Spot Trade Layout: midgraph_col) */}
                        <div className="col-12 col-lg-8 col-xl-9 col-xxl-9 midgraph_col">
                            {/* Top Stats Card (Same classes as Spot Trade) */}
                            <div className="trade_card p-2 overflow_card mb-1">
                                <div className="headline_left__lBBPY">
                                    <div className="headline_left__lBBPY_leftmain d-flex align-items-center">
                                        {/* Coin Name with Down Arrow (Click opens pairs dropdown on desktop, popup on mobile) */}
                                        <div
                                            className="headline_symbolName__KfmIZ mt_tr_pr cursor-pointer"
                                            onClick={() => {
                                                if (
                                                    typeof window !== "undefined" &&
                                                    (window.innerWidth <= 991 ||
                                                        (window.matchMedia && window.matchMedia("(max-width: 991px)").matches))
                                                ) {
                                                    setShowMobileFavouritesPopup(true);
                                                    return;
                                                }
                                                setIsFavouritesOpen((v) => !v);
                                            }}
                                            title="Click to change pair"
                                        >
                                            <div className="headline_bigName__dspVW">
                                                <img
                                                    alt={SelectedCoin?.base_currency || "BTC"}
                                                    src={coinIconSrc(SelectedCoin?.icon_path, SelectedCoin?.base_currency)}
                                                    width="24"
                                                    className="img-fluid round_img"
                                                    onError={(e) => {
                                                        e.target.src = "/images/icon/btc.png";
                                                    }}
                                                />
                                            </div>

                                            <div>
                                                <div className="headline_bigName__dspVW">
                                                    <h1>
                                                        {activePairSymbol}
                                                        <i className="ri-arrow-down-s-line ms-1" />
                                                    </h1>
                                                </div>
                                                <div className="headline_etfDisplay__P4Hdv">
                                                    <span>{SelectedCoin?.base_currency_fullname || baseCoin}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Mobile Price & 24h Change (Identical to Spot Trade) */}
                                        <div className="headline_leftItem__7BFYq headline_latestPrice__AYXu0 d-lg-none ms-0 mt-1">
                                            <div>
                                                <span
                                                    className={`headline_title__x1csO font-weight-boldd ${isPricePositive ? "text-green" : "text-danger"}`}
                                                >
                                                    {displayLastForHeader || SelectedCoin?.buy_price || "—"}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="headline_leftItem__7BFYq ms-0 d-flex d-lg-none">
                                            <div className="headline_withBorder__a6ZD2 me-1">24h Change</div>
                                            <div
                                                className={`headline_title__x1csO font-weight-boldd ${priceChange >= 0 ? "text-green" : "text-danger"}`}
                                            >
                                                {priceChange >= 0 ? "+" : ""}
                                                {parseFloat(priceChange || 0).toFixed(2)}%
                                            </div>
                                        </div>

                                        {/* 24h stats strip in header (Identical to Spot Trade) */}
                                        <div className="scroll-subtabs_scrollSubInfo__T5nZF headline_left__lBBPY_rightmain ms-3">
                                            <div className="scroll-subtabs_tabs__Prom8">
                                                <div className="scroll-subtabs_subMarketWrap__XVmHp">
                                                    <div className="headline_extendInfoWrapper__dooIS d-flex align-items-center">
                                                        <div className="headline_leftItem__7BFYq d-none d-lg-block">
                                                            <div className="headline_withBorder__a6ZD2">
                                                                Last Price ({SelectedCoin?.quote_currency || "USDT"})
                                                            </div>
                                                            <span
                                                                className={`headline_title__x1csO font-weight-boldd ${isPricePositive ? "text-green" : "text-danger"
                                                                    }`}
                                                            >
                                                                {displayLastForHeader || SelectedCoin?.buy_price || "—"}
                                                            </span>
                                                        </div>
                                                        <div className="headline_leftItem__7BFYq d-none d-lg-block">
                                                            <div className="headline_withBorder__a6ZD2">24h Change</div>
                                                            <div
                                                                className={`headline_title__x1csO font-weight-boldd ${priceChange >= 0 ? "text-green" : "text-danger"
                                                                    }`}
                                                            >
                                                                {priceChange >= 0 ? "+" : ""}
                                                                {parseFloat(priceChange || 0).toFixed(2)}%
                                                            </div>
                                                        </div>
                                                        <div className="headline_leftItem__7BFYq d-none d-lg-block">
                                                            <div className="headline_withBorder__a6ZD2">
                                                                24h High ({SelectedCoin?.quote_currency || "USDT"})
                                                            </div>
                                                            <div className="headline_title__x1csO text-success font-weight-boldd">
                                                                {SelectedCoin?.high || "—"}
                                                            </div>
                                                        </div>
                                                        <div className="headline_leftItem__7BFYq d-none d-lg-block">
                                                            <div className="headline_withBorder__a6ZD2">
                                                                24h Low ({SelectedCoin?.quote_currency || "USDT"})
                                                            </div>
                                                            <div className="headline_title__x1csO text-danger font-weight-boldd">
                                                                {SelectedCoin?.low || "—"}
                                                            </div>
                                                        </div>
                                                        <div className="headline_leftItem__7BFYq d-none d-lg-block">
                                                            <div className="headline_withBorder__a6ZD2">
                                                                24h Volume ({SelectedCoin?.base_currency || "BTC"})
                                                            </div>
                                                            <div className="headline_title__x1csO font-weight-boldd">
                                                                {SelectedCoin?.volume || "—"}
                                                            </div>
                                                        </div>
                                                        <div className="headline_leftItem__7BFYq d-none d-lg-block">
                                                            <div className="headline_withBorder__a6ZD2">
                                                                24h Volume ({SelectedCoin?.quote_currency || "USDT"})
                                                            </div>
                                                            <div className="headline_title__x1csO font-weight-boldd">
                                                                {SelectedCoin?.volumeQuote || "—"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Chart Card (Exact Spot Trade wrapper: trade_card trade_chart p-0) */}
                            <div className="trade_card trade_chart p-0 mb-1">
                                <div className="treade_card_header tch_main_tab">
                                    <div className="card_header_title cursor-pointer active">Chart</div>
                                </div>
                                <div id="tab_1" className="cc_tab">
                                    <TVChartContainer symbol={activePairSymbol} />
                                </div>
                            </div>

                            {/* Bottom Orders Table (Exact Spot Trade Tabs: spot_orders_primary_tabs) */}

                    <div className="trade_account_summary_assets">
                        <div className="trade_summary_table_lft mt-0 position_order trade_summary_table_lft_position_order">
                            <div className="top_th_easyop border-0 spot_orders_top">
                                <div className="spot_orders_primary_tabs_scrollWrap">
                                    <ul className="position_list spot_orders_primary_tabs">
                                        <li
                                            className={`nav-item positions ${bottomTab === "active" ? "active" : ""}`}
                                            role="presentation"
                                        >
                                            <button type="button" onClick={() => setBottomTab("active")}>
                                                Active requests
                                            </button>
                                        </li>
                                        <li
                                            className={`nav-item orderHistory ${bottomTab === "history" ? "active" : ""}`}
                                            role="presentation"
                                        >
                                            <button type="button" onClick={() => setBottomTab("history")}>
                                                History
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="otc_hist_body">
                                <div className="otc_hist_scroll">
                                    <table className={`otc_hist_table ${bottomTab === "history" ? "otc_hist_table--history" : "otc_hist_table--active"}`}>
                                        <thead>
                                            {bottomTab === "history" ? (
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Ticket ID</th>
                                                    <th>Type</th>
                                                    <th>Symbol</th>
                                                    <th>Side</th>
                                                    <th>Amount</th>
                                                    <th>Traded Amount</th>
                                                    <th>Quote price</th>
                                                    <th>Status</th>
                                                    <th>Action</th>
                                                </tr>
                                            ) : (
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Ticket ID</th>
                                                    <th>Type</th>
                                                    <th>Symbol</th>
                                                    <th>Side</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>Limit Price</th>
                                                    <th>Progress</th>
                                                    <th>Average price</th>
                                                    <th>Filled / Total Amount</th>
                                                    <th>Action</th>
                                                </tr>
                                            )}
                                        </thead>
                                        {displayTableRows.length > 0 ? (
                                            <tbody>
                                                {displayTableRows.map((row) => (
                                                    bottomTab === "history" ? (
                                                        <tr key={row.id}>
                                                            <td>{row.date}</td>
                                                            <td>{row.ticketId}</td>
                                                            <td>{row.type}</td>
                                                            <td>{row.pair}</td>
                                                            <td>
                                                                <span
                                                                    className={`badge ${row.side.toLowerCase() === "sell"
                                                                        ? "badge-soft-danger text-danger"
                                                                        : "badge-soft-success text-green"
                                                                        }`}
                                                                >
                                                                    {row.side}
                                                                </span>
                                                            </td>
                                                            <td>{row.pay}</td>
                                                            <td>{row.tradedAmount}</td>
                                                            <td>{row.quotePrice}</td>
                                                            <td>{row.status}</td>
                                                            <td>
                                                                {row.view ? (
                                                                    <RfqRowMenu
                                                                        open={actionMenuId === row.id}
                                                                        onToggle={(next) => setActionMenuId(next ? row.id : "")}
                                                                        onView={() => {
                                                                            setActionMenuId("");
                                                                            setViewRfq(row.view);
                                                                        }}
                                                                        onCancel={row.source ? () => openCancelConfirm(row.source) : null}
                                                                    />
                                                                ) : "—"}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        <tr key={row.id}>
                                                            <td>{row.date}</td>
                                                            <td>{row.ticketId}</td>
                                                            <td>{row.type}</td>
                                                            <td>{row.pair}</td>
                                                            <td>
                                                                <span
                                                                    className={`badge ${row.side.toLowerCase() === "sell"
                                                                        ? "badge-soft-danger text-danger"
                                                                        : "badge-soft-success text-green"
                                                                        }`}
                                                                >
                                                                    {row.side}
                                                                </span>
                                                            </td>
                                                            <td>{row.pay}</td>
                                                            <td>{row.status}</td>
                                                            <td>{row.limitPrice}</td>
                                                            <td>{row.progress}</td>
                                                            <td>{row.averagePrice}</td>
                                                            <td>{row.filledTotal}</td>
                                                            <td>
                                                                {row.view ? (
                                                                    <RfqRowMenu
                                                                        open={actionMenuId === row.id}
                                                                        onToggle={(next) => setActionMenuId(next ? row.id : "")}
                                                                        onView={() => {
                                                                            setActionMenuId("");
                                                                            setViewRfq(row.view);
                                                                        }}
                                                                        onCancel={row.source ? () => openCancelConfirm(row.source) : null}
                                                                    />
                                                                ) : "—"}
                                                            </td>
                                                        </tr>
                                                    )
                                                ))}
                                            </tbody>
                                        ) : null}
                                    </table>
                                </div>
                                {displayTableRows.length === 0 ? (
                                    <div className="otc_hist_empty" role="status">
                                        <img
                                            src="/images/no-data.svg"
                                            alt=""
                                            width={120}
                                            height={144}
                                            className="spot_orders_empty_telescope"
                                        />
                                        {bottomTab === "history" && desk.tradesLoading ? (
                                            <p>Loading desk history…</p>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>

                                {/* Mobile View: OrderMobileCardShell exactly like Spot Trade */}
                                <div className="otc_hist_mobile">
                                    <div className="d-flex datalist_mbl flex-column">
                                        {displayTableRows.length > 0 ? (
                                            displayTableRows.map((row, index) => (
                                                <OrderMobileCardShell
                                                    key={row?.id || `otc-${index}`}
                                                    desktopColumnCount={bottomTab === "history" ? 10 : 12}
                                                    popupTitle="OTC Order details"
                                                    includeActionsInPopup={false}
                                                    heroPrimary={
                                                        <div className="d-flex align-items-center gap-2">
                                                            <img
                                                                src={coinIconSrc(null, row?.pair?.split("/")?.[0])}
                                                                alt=""
                                                                width="18"
                                                                height="18"
                                                                className="round_img"
                                                                onError={(e) => {
                                                                    e.target.src = "/images/icon/btc.png";
                                                                }}
                                                            />
                                                            <span>{row?.pair}</span>
                                                        </div>
                                                    }
                                                    heroSecondary={
                                                        <span
                                                            className={`orders-mobCard__subtype ${row?.side?.toLowerCase() === "buy"
                                                                ? "orders-mobCard__subtype--buy"
                                                                : "orders-mobCard__subtype--sell"
                                                                }`}
                                                        >
                                                            {row?.side} {row?.type || "RFQ"}
                                                        </span>
                                                    }
                                                    heroDatetime={bottomTab === "history" ? (row?.date || "") : (row?.time || "")}
                                                >
                                                    {bottomTab === "history" ? (
                                                        <>
                                                            <div className="so-mobileCard__row">
                                                                <span>Date</span>
                                                                <span>{row?.date}</span>
                                                            </div>
                                                            <div className="so-mobileCard__row">
                                                                <span>Ticket ID</span>
                                                                <span>{row?.ticketId}</span>
                                                            </div>
                                                            <div className="so-mobileCard__row">
                                                                <span>Type</span>
                                                                <span>{row?.type}</span>
                                                            </div>
                                                            <div className="so-mobileCard__row">
                                                                <span>Symbol</span>
                                                                <span>{row?.pair}</span>
                                                            </div>
                                                            <div className="so-mobileCard__row">
                                                                <span>Side</span>
                                                                <span>{row?.side}</span>
                                                            </div>
                                                            <div className="so-mobileCard__row">
                                                                <span>Amount</span>
                                                                <span>{row?.pay}</span>
                                                            </div>
                                                            <div className="so-mobileCard__row">
                                                                <span>Traded Amount</span>
                                                                <span>{row?.tradedAmount}</span>
                                                            </div>
                                                            <div className="so-mobileCard__row">
                                                                <span>Quote price</span>
                                                                <span>{row?.quotePrice}</span>
                                                            </div>
                                                            <div className="so-mobileCard__row">
                                                                <span>Status</span>
                                                                <span>{row?.status}</span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                    <div className="so-mobileCard__row">
                                                        <span>Date</span>
                                                        <span>{row?.date}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Ticket ID</span>
                                                        <span>{row?.ticketId}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Type</span>
                                                        <span>{row?.type}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Symbol</span>
                                                        <span>{row?.pair}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Side</span>
                                                        <span>{row?.side}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Amount</span>
                                                        <span>{row?.pay}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Status</span>
                                                        <span>{row?.status}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Limit Price</span>
                                                        <span>{row?.limitPrice}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Progress</span>
                                                        <span>{row?.progress}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Average price</span>
                                                        <span>{row?.averagePrice}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Filled / Total Amount</span>
                                                        <span>{row?.filledTotal}</span>
                                                    </div>
                                                        </>
                                                    )}
                                                    {row?.view ? (
                                                        <div className="so-mobileCard__actions otc-rfq-card-actions">
                                                            <RfqRowMenu
                                                                open={actionMenuId === row.id}
                                                                onToggle={(next) => setActionMenuId(next ? row.id : "")}
                                                                onView={() => {
                                                                    setActionMenuId("");
                                                                    setViewRfq(row.view);
                                                                }}
                                                                onCancel={row?.source ? () => openCancelConfirm(row.source) : null}
                                                            />
                                                        </div>
                                                    ) : null}
                                                </OrderMobileCardShell>
                                            ))
                                        ) : (
                                            <div className="no-data-wrapper w-100">
                                                <div className="spot_orders_empty_state" role="status">
                                                    <img
                                                        src="/images/no-data.svg"
                                                        alt=""
                                                        width={120}
                                                        height={144}
                                                        className="spot_orders_empty_telescope"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                        {/* Right Sidebar Column (Exact Spot Trade Layout: mmn_btm_minus_spc) */}
                        <div className="col-12 col-lg-4 col-xl-3 col-xxl-3 mmn_btm_minus_spc letsidebar_tradetbl">
                            {/* Mobile Buy / Sell Action Bar (Exact Spot Trade: bs_tab_row d-lg-none) */}
                            <div className="bs_tab_row d-lg-none">
                                <div className="row gx-3">
                                    <div className="col-6">
                                        <button
                                            type="button"
                                            className="btn btn-success btn-block w-100"
                                            onClick={() => {
                                                handleSwitchOrderSide("BUY");
                                                setShowBuySellTab("buy");
                                            }}
                                        >
                                            <span>Buy</span>
                                        </button>
                                    </div>
                                    <div className="col-6">
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-block w-100"
                                            onClick={() => {
                                                handleSwitchOrderSide("SELL");
                                                setShowBuySellTab("sell");
                                            }}
                                        >
                                            <span>Sell</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Buy/Sell Drawer Modal (Exact Spot Trade: bs_dropbox) */}
                            <div
                                className={`bs_dropbox d-lg-block ${!showBuySellTab ? "d-none" : ""}`}
                                onClick={() => setShowBuySellTab("")}
                            >
                                <div className="row w-100 m-0" onClick={(e) => e.stopPropagation()}>
                                    <div className="col-12 p-0">
                                        {/* Card 1: OTC Trading Form (buysell_card buysell_two) */}
                                        <div className="trade_card trade_chart buysell_card buysell_two mb-2 p-3" ref={quoteFormRef}>
                                            {/* Header */}
                                            <div className="d-flex align-items-center justify-content-between trading_headingotr">
                                                <h5 className="mb-0">
                                                    OTC Trading
                                                </h5>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="otc-header-pair" ref={headerPairRef}>
                                                        <button
                                                            type="button"
                                                            className="otc-header-pair__btn"
                                                            aria-haspopup="listbox"
                                                            aria-expanded={headerPairsOpen}
                                                            onClick={() => setHeaderPairsOpen((open) => !open)}
                                                        >
                                                            <img
                                                                alt=""
                                                                src={coinIconSrc(headerPair?.icon_path, deskSymbol.split("/")[0])}
                                                                width="24"
                                                                className="img-fluid round_img"
                                                                onError={(e) => {
                                                                    e.target.src = "/images/icon/btc.png";
                                                                }}
                                                            />
                                                            <span className="otc-header-pair__text">
                                                                <span className="otc-header-pair__symbol">
                                                                    {deskSymbol}
                                                                    <i className={`ri-arrow-${headerPairsOpen ? "up" : "down"}-s-line`} />
                                                                </span>
                                                                <span className="otc-header-pair__name">{headerPairName}</span>
                                                            </span>
                                                        </button>
                                                        {headerPairsOpen && (
                                                            <div
                                                                className="otc-pair-drawer-backdrop"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setHeaderPairsOpen(false);
                                                                }}
                                                            />
                                                        )}
                                                        <TradePairsSection
                                                            setIsFavouritesOpen={setHeaderPairsOpen}
                                                            isFavouritesOpen={headerPairsOpen}
                                                            closeOnLeave={false}
                                                            token={token}
                                                            coinFilter={tradingCoinFilter}
                                                            setcoinFilter={setTradingCoinFilter}
                                                            CoinPairDetails={tradingPairs}
                                                            search={tradingSearch}
                                                            setsearch={setTradingSearch}
                                                            favCoins={favCoins}
                                                            SelectedCoin={headerPair}
                                                            handleSelectCoin={handleTradingPair}
                                                            coinIconSrc={coinIconSrc}
                                                            handleAddFav={handleAddFav}
                                                            title="Select Pair"
                                                            onClose={() => setHeaderPairsOpen(false)}
                                                            hideQuoteTabs
                                                        />
                                                    </div>
                                                    <div className="d-lg-none mt-0">
                                                        <span
                                                            className="cursor-pointer"
                                                            onClick={() => setShowBuySellTab("")}
                                                        >
                                                            <i
                                                                className="ri-close-line"
                                                                style={{ fontSize: "24px", color: "#848E9C" }}
                                                            />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Buy / Sell Switch (buysell_heder custom-tabs) */}
                                            <div className="treade_card_header buysell_heder d-block p-0 mb-3">
                                                <ul className="nav custom-tabs padding-0 w-100 d-flex">
                                                    <li className="buysell-tab buy-tab flex-fill">
                                                        <a
                                                            href="#/"
                                                            className={orderSide === "BUY" ? "active" : ""}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleSwitchOrderSide("BUY");
                                                                setShowBuySellTab("buy");
                                                            }}
                                                        >
                                                            <button type="button" className="w-100">
                                                                <span>Buy</span>
                                                            </button>
                                                        </a>
                                                    </li>
                                                    <li className="sell-tab flex-fill">
                                                        <a
                                                            href="#/"
                                                            className={orderSide === "SELL" ? "active" : ""}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleSwitchOrderSide("SELL");
                                                                setShowBuySellTab("sell");
                                                            }}
                                                        >
                                                            <button type="button" className="w-100">
                                                                <span>Sell</span>
                                                            </button>
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>

                                            {/* Spot Trade Form: wrapped in form.buysellform so all spot input styles apply automatically */}
                                            <form className={`buysellform ${orderSide === "BUY" ? "data-buy buy_spot_form" : "data-sell sell_spot_form"}`} onSubmit={(e) => e.preventDefault()}>
                                                <div className="form-group mb-2 trade_amount_field_wrap">
                                                    <div className="otc-amount-label-row">
                                                        <label>Amount</label>
                                                        <span className="otc-balance-tip">
                                                            Available for OTC: {availableText}{" "}
                                                            <span className="otc-balance-tip__icon">
                                                                <i className="ri-information-line" aria-hidden="true" />
                                                                <span className="otc-balance-tip__text" role="tooltip">
                                                                    This is the available balance in your Spot wallet. OTC uses the same balance.
                                                                </span>
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <div className="input-group trade_amount_field_limit">
                                                        <input
                                                            type="text"
                                                            className="form-control otc-amount-input"
                                                            aria-label="Amount"
                                                            inputMode="decimal"
                                                            autoComplete="off"
                                                            placeholder={desk.minBase || "0"}
                                                            value={desk.amount || ""}
                                                            onChange={(e) => desk.setAmount?.(e.target.value.replace(/[^\d.]/g, ""))}
                                                        />
                                                        <div className="input-group-text text-start trade_amt_denom_addon trade_amount_coin_badge d-flex align-items-center gap-2">
                                                            <TradeAmountDenomSelect
                                                                value={payDenom}
                                                                onChange={(next) => desk.setAmountCoin?.(next === "QUOTE" ? quoteCoin : baseCoin)}
                                                                baseSymbol={baseCoin}
                                                                quoteSymbol={quoteCoin}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="otc_max_btn"
                                                                onClick={() => desk.onMax?.()}
                                                            >
                                                                MAX
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {desk.amountApprox || desk.minQuoteLabel ? (
                                                        <div className="quote-amount-approx">
                                                            {desk.minQuoteLabel ? <span>{desk.minQuoteLabel}</span> : null}
                                                            {desk.amountApprox ? <span>{desk.amountApprox}</span> : null}
                                                        </div>
                                                    ) : null}
                                                    {desk.amountError ? (
                                                        <p className="otc-amount-error">{desk.amountError}</p>
                                                    ) : desk.amountWarning ? (
                                                        <p className="otc-amount-warning">{desk.amountWarning}</p>
                                                    ) : null}
                                                </div>

                                                {/* Request for Quote Button */}
                                                <div className="my-3">
                                                    {desk.requestWindowOpen ? (
                                                        <p className="otc-amount-warning">{desk.requestWindowMessage}</p>
                                                    ) : null}
                                                    {desk.kycPending ? (
                                                        <button type="button" className="requestquotebtn btn w-100 py-2" disabled>
                                                            Checking account…
                                                        </button>
                                                    ) : desk.isLoggedIn && !desk.kycVerified ? (
                                                        <button
                                                            type="button"
                                                            onClick={desk.openKyc}
                                                            className="requestquotebtn btn w-100 py-2"
                                                        >
                                                            {desk.kycCtaLabel || "Submit Kyc"}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="requestquotebtn btn w-100 py-2"
                                                            onClick={() => desk.submitRfq?.()}
                                                            disabled={
                                                              desk.isLoggedIn && (
                                                                desk.submitting
                                                                || desk.requestWindowOpen
                                                                || !/^\d+(\.\d+)?$/.test(String(desk.amount || "").trim())
                                                                || Number(desk.amount) <= 0
                                                                || Boolean(desk.amountError)
                                                              )
                                                            }
                                                        >
                                                            {desk.submitting ? "Requesting…" : "Request for Quote"}
                                                        </button>
                                                    )}
                                                </div>
                                            </form>

                                            {/* Current Ticket Details */}
                                            <div className="ticketbl pt-1 mt-0">
                                                <div className="d-flex align-items-center justify-content-between mb-2 otc-current-ticket" ref={activeTicketRef}>
                                                    <button
                                                        type="button"
                                                        className="otc-current-ticket__btn"
                                                        aria-haspopup="listbox"
                                                        aria-expanded={activeTicketOpen}
                                                        disabled={activeTickets.length === 0}
                                                        onClick={() => setActiveTicketOpen((open) => !open)}
                                                    >
                                                        <h6 className="font-weight-bold mb-0">Current Ticket</h6>
                                                        {activeTickets.length > 0 ? <i className="ri-arrow-down-s-line" aria-hidden="true" /> : null}
                                                    </button>
                                                    <span className="badge-soft-warning text-warning" style={{ fontSize: "11px" }}>
                                                        {desk.view === "expired"
                                                          ? (String(desk.rfq?.status || "").toUpperCase() === "REJECTED_TIMEOUT" ? "REJECTED_TIMEOUT" : "EXPIRED")
                                                          : (desk.rfq?.status || "—")}
                                                    </span>
                                                    {activeTicketOpen && activeTickets.length > 0 ? (
                                                        <div className="otc-current-ticket__menu" role="listbox">
                                                            {activeTickets.map((ticket) => (
                                                                <button
                                                                    key={ticket.id}
                                                                    type="button"
                                                                    role="option"
                                                                    aria-selected={ticket.id === String(desk.rfq?.id || "")}
                                                                    className={ticket.id === String(desk.rfq?.id || "") ? "is-current" : ""}
                                                                    onClick={() => {
                                                                        setActiveTicketOpen(false);
                                                                        desk.selectActiveRequest?.(ticket.rfq, ticket.quote);
                                                                    }}
                                                                >
                                                                    <span>{ticket.pair}</span>
                                                                    <span>{ticket.side}</span>
                                                                    <span>{ticket.pay}</span>
                                                                    <span>{ticket.status}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="d-flex flex-column gap-2 small listotctrade">
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted">Ticker</span>
                                                        <span className="font-weight-bold">{deskSymbol}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted">Order type</span>
                                                        <span className="font-weight-bold">OTC RFQ</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted">Date</span>
                                                        <span className="font-weight-bold">{desk.rfq ? formatWhen(desk.rfq.createdAt) : "—"}</span>
                                                    </div>
                                                    {desk.view === "awaiting" && desk.rfq?.expiresAt ? (
                                                        <div className="d-flex justify-content-between">
                                                            <span className="text-muted">Valid until</span>
                                                            <span className="font-weight-bold otc-valid-until">
                                                                {formatWhen(desk.rfq.expiresAt)}
                                                                {desk.requestLeftMs > 0 ? ` · ${formatLeft(desk.requestLeftMs)} left` : " · closing"}
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                </div>

                                                {/* Quote Requested Info Box */}
                                                {desk.view === "awaiting" ? (
                                                    <div className="quotedeskawaiting">
                                                        <div className="headingh6 mb-1">
                                                            Quote requested, awaiting desk
                                                        </div>
                                                        <div className="textp">
                                                            Your request is with the desk
                                                            {desk.rfq?.expiresAt ? (
                                                                <>
                                                                    {" "}and stays valid until{" "}
                                                                    <span className="otc-valid-until">
                                                                        {formatWhen(desk.rfq.expiresAt)}
                                                                        {desk.requestLeftMs > 0 ? ` · ${formatLeft(desk.requestLeftMs)} left` : ""}
                                                                    </span>
                                                                    .
                                                                </>
                                                            ) : (
                                                                ". This page checks status every few seconds until a quote is received."
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {desk.view === "expired" ? (
                                                    <div className="quotedeskawaiting">
                                                        <div className="headingh6 mb-1">
                                                            Request expired
                                                        </div>
                                                        <div className="textp">
                                                            This request is no longer with the desk. Enter an amount to request a new quote.
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {desk.view === "quote" ? (
                                                    <div className="quotedeskawaiting">
                                                        <div className="headingh6 mb-1">
                                                            Firm quote ready
                                                        </div>
                                                        <div className="textp">
                                                            The desk sent a quote. Review every detail in the popup and accept before it expires.
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {!desk.rfq ? (
                                                    <div className="quotedeskawaiting">
                                                        <div className="headingh6 mb-1">
                                                            No open request
                                                        </div>
                                                        <div className="textp">
                                                            Enter an amount and request a quote. The ticket updates from the desk.
                                                        </div>
                                                    </div>
                                                ) : null}

                                                {/* Ticket Execution Summary */}
                                                <div className="d-flex flex-column gap-2 small listotctrade mb-3">
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted">Side</span>
                                                        <span className={`font-weight-bold ${orderSide === "BUY" ? "text-green buybtn" : "text-danger sellbtn"}`}>
                                                            {desk.rfq?.side || orderSide}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted">You pay</span>
                                                        <span className="font-weight-bold">{desk.rfq ? moneyLine(desk.rfq.pay_amount, desk.rfq.pay_asset) : "—"}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted">You get</span>
                                                        <span className="font-weight-bold">{desk.quote ? moneyLine(desk.quote.get_amount, desk.quote.get_asset) : desk.rfq ? moneyLine(desk.rfq.get_amount, desk.rfq.get_asset) : "—"}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted">Rate</span>
                                                        <span className="font-weight-bold">{desk.quote?.rate || "The desk sets the rate"}</span>
                                                    </div>
                                                    {desk.quote?.expiresAt ? (
                                                        <div className="d-flex justify-content-between">
                                                            <span className="text-muted">Quote valid until</span>
                                                            <span className="font-weight-bold">
                                                                {formatWhen(desk.quote.expiresAt)}
                                                                {desk.view === "quote" ? ` · ${formatLeft(desk.countdownMs)} left` : ""}
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                </div>

                                                {desk.view === "awaiting" ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary cancel_order_btn"
                                                        onClick={() => openCancelConfirm(desk.rfq)}
                                                        disabled={desk.submitting}
                                                    >
                                                        Cancel request
                                                    </button>
                                                ) : null}
                                                {desk.view === "quote" ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary cancel_order_btn"
                                                        onClick={() => setQuotePopupOpen(true)}
                                                    >
                                                        Review quote
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>




                </div>
            </div>

            {viewRfq && typeof document !== "undefined" ? createPortal(
                <div className="otc-accept-overlay" role="presentation" onClick={() => setViewRfq(null)}>
                    <div
                        className="otc-accept-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="otc-view-rfq-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="otc-accept-head">
                            <div>
                                <p className="otc-accept-kicker">Quote request</p>
                                <h2 id="otc-view-rfq-title">{viewRfq.pair || "RFQ"}</h2>
                            </div>
                            <button type="button" className="otc-accept-close" onClick={() => setViewRfq(null)} aria-label="Close">
                                ×
                            </button>
                        </div>
                        <div className="otc-accept-rows">
                            <div className="otc-accept-row"><span>Ticket</span><span>{viewRfq.ticketId}</span></div>
                            <div className="otc-accept-row"><span>Side</span><span>{viewRfq.side}</span></div>
                            <div className="otc-accept-row"><span>You pay</span><span>{viewRfq.pay}</span></div>
                            <div className="otc-accept-row"><span>You get</span><span>{viewRfq.receive}</span></div>
                            <div className="otc-accept-row"><span>Status</span><span>{viewRfq.status}</span></div>
                            <div className="otc-accept-row"><span>Request rate</span><span>{formatAmount(viewRfq.requestRate) || "—"}</span></div>
                            <div className="otc-accept-row"><span>Desk rate</span><span>{viewRfq.rate ? formatAmount(viewRfq.rate) : "The desk sets the rate"}</span></div>
                            <div className="otc-accept-row"><span>Opened</span><span>{formatWhen(viewRfq.createdAt)}</span></div>
                            {viewRfq.expiresAt ? (
                                <div className="otc-accept-row"><span>Valid until</span><span>{formatWhen(viewRfq.expiresAt)}</span></div>
                            ) : null}
                        </div>
                    </div>
                </div>,
                document.body
            ) : null}

            <OtcCancelRfqModal
                open={cancelConfirmOpen && String(cancelTarget?.status || "").toUpperCase() === "OPEN"}
                rfq={cancelTarget}
                submitting={desk.submitting}
                onClose={() => {
                    if (desk.submitting) return;
                    setCancelConfirmOpen(false);
                }}
                onConfirm={async () => {
                    const cancelled = await desk.cancelRfq?.(cancelTarget);
                    if (cancelled) setCancelConfirmOpen(false);
                }}
            />

            <OtcAcceptQuoteModal
                open={quotePopupOpen && desk.view === "quote"}
                quote={desk.quote}
                rfq={desk.rfq}
                countdownMs={desk.countdownMs}
                submitting={desk.submitting}
                kycPending={desk.kycPending}
                kycVerified={desk.kycVerified}
                kycCtaLabel={desk.kycCtaLabel}
                onOpenKyc={desk.openKyc}
                limitError={desk.acceptLimitError}
                onAccept={() => desk.acceptQuote?.()}
                onClose={() => setQuotePopupOpen(false)}
            />

            {/* Mobile Favourites Popup (Bottom Sheet Modal like Spot Trade) */}
            {showMobileFavouritesPopup && (
                <div
                    className="mobile-favourites-popup-overlay"
                    onClick={() => setShowMobileFavouritesPopup(false)}
                >
                    <div
                        className="mobile-favourites-popup pairpopup"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mobile-favourites-popup-header">
                            <h4>Select Pair</h4>
                            <button
                                type="button"
                                className="mobile-favourites-close-btn"
                                onClick={() => setShowMobileFavouritesPopup(false)}
                            >
                                <i className="ri-close-line" />
                            </button>
                        </div>
                        <div className="mobile-favourites-popup-content">
                            <div className="spotLists">
                                {/* Search */}
                                <div className="spot-list-search">
                                    <div className="ivu-input">
                                        <i className="ri-search-2-line" />
                                        <input
                                            autoComplete="off"
                                            spellCheck="false"
                                            type="search"
                                            placeholder="Search"
                                            onChange={(e) => setsearch(e.target.value)}
                                            value={search}
                                        />
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="price_card table-responsive">
                                    <table className="table table-sm table-borderless mb-0 orderbook-table">
                                        <thead>
                                            <tr>
                                                <th>Pair</th>
                                                <th className="text-end">Price</th>
                                                <th className="text-end">Change</th>
                                            </tr>
                                        </thead>
                                        <tbody className="price_card_body">
                                            {CoinPairDetails &&
                                                CoinPairDetails.map((data, index) => {
                                                    if (
                                                        search &&
                                                        !data?.base_currency?.toLowerCase().includes(search.toLowerCase()) &&
                                                        !data?.quote_currency?.toLowerCase().includes(search.toLowerCase()) &&
                                                        !data?.base_currency_fullname?.toLowerCase().includes(search.toLowerCase())
                                                    ) {
                                                        return null;
                                                    }

                                                    const isActive =
                                                        SelectedCoin?.base_currency === data?.base_currency &&
                                                        SelectedCoin?.quote_currency === data?.quote_currency;

                                                    return (
                                                        <tr
                                                            key={data?._id || index}
                                                            className={isActive ? "active" : ""}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => {
                                                                handleSelectCoin(data);
                                                                setShowMobileFavouritesPopup(false);
                                                            }}
                                                        >
                                                            {/* Pair */}
                                                            <td>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <img
                                                                        src={coinIconSrc(data?.icon_path)}
                                                                        alt=""
                                                                        className="img-fluid me-1 round_img"
                                                                    />
                                                                    <div className="d-flex flex-column pair-color">
                                                                        {`${data?.base_currency}/${data?.quote_currency}`}
                                                                        <span className="tokensubcnt">
                                                                            {data?.base_currency_fullname}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* Price */}
                                                            <td className="text-end">
                                                                <div className="d-flex flex-column pair-color">
                                                                    <span>{data?.buy_price}</span>
                                                                    <span className="tokensubcnt">${data?.buy_price}</span>
                                                                </div>
                                                            </td>

                                                            {/* Change + Star */}
                                                            <td className="text-end">
                                                                <div className="d-flex justify-content-end align-items-center gap-2">
                                                                    <div className="d-flex flex-column pair-color text-end">
                                                                        <span
                                                                            className={
                                                                                data?.change_percentage >= 0
                                                                                    ? "text-green"
                                                                                    : "text-danger"
                                                                            }
                                                                        >
                                                                            {data?.change_percentage >= 0
                                                                                ? `+${Number(parseFloat(data?.change_percentage)?.toFixed(5))}`
                                                                                : Number(parseFloat(data?.change_percentage)?.toFixed(5))}
                                                                            %
                                                                        </span>
                                                                        <span className="tokensubcnt">
                                                                            {parseFloat(data?.change?.toFixed(5)) || 0}
                                                                        </span>
                                                                    </div>

                                                                    {token && (
                                                                        <i
                                                                            className={
                                                                                favCoins.includes(data?._id)
                                                                                    ? "ri ri-star-fill ri-xl"
                                                                                    : "ri ri-star-line ri-xl"
                                                                            }
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAddFav(data?._id);
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

